/**
 * Third column of the MCP guardrails panel: the fields of one allowed
 * collection, each with a checkbox (checked = reaches Claude, unchecked =
 * hidden). Fields hidden on every collection are shown locked with a
 * "global" tag. Search filters the list; the bottom input hides a field the
 * sample did not show.
 *
 * Mount it with `key={collection}` so the search and the manual input reset
 * when the collection changes.
 */
import React, { useState } from 'react';
import { Button, Checkbox, Empty, Input, Space, Spin, Tag, Typography } from 'antd';
import { PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { countAllowedFields, setFieldAllowed, setFieldsAllowed } from '../../utils/mcpGuardrails';

const { Text } = Typography;

const DIVIDER = '1px solid rgba(5, 5, 5, 0.06)';

interface Props {
  /** null = no collection selected yet. */
  collection: string | null;
  /** Every known field of the collection (sampled + hidden), sorted. */
  fields: string[];
  /** Hidden for this collection only. */
  hidden: string[];
  /** Hidden on every collection (locked here). */
  globalHidden: ReadonlySet<string>;
  loading: boolean;
  height: number;
  onChange: (hidden: string[]) => void;
}

const McpFieldPicker: React.FC<Props> = ({ collection, fields, hidden, globalHidden, loading, height, onChange }) => {
  const [search, setSearch] = useState('');
  const [manual, setManual] = useState('');

  const visible = fields.filter((f) => f.toLowerCase().includes(search.toLowerCase()));
  const editable = visible.filter((f) => !globalHidden.has(f));
  const allAllowed = editable.length > 0 && editable.every((f) => !hidden.includes(f));
  const someAllowed = editable.some((f) => !hidden.includes(f));

  function addManual(): void {
    const field = manual.trim();
    if (field && !globalHidden.has(field)) onChange(setFieldAllowed(hidden, field, false));
    setManual('');
  }

  return (
    <div
      data-testid="mcp-field-picker"
      style={{
        width: '100%',
        height,
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid rgba(5, 5, 5, 0.12)',
        borderRadius: 8,
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          padding: '8px 12px',
          borderBottom: DIVIDER,
        }}
      >
        {collection ? (
          <>
            <Checkbox
              aria-label="Permitir todos los campos visibles"
              checked={allAllowed}
              indeterminate={someAllowed && !allAllowed}
              disabled={editable.length === 0}
              onChange={(e) => onChange(setFieldsAllowed(hidden, editable, e.target.checked))}
              style={{ flex: 1, minWidth: 0 }}
            >
              <Text ellipsis style={{ maxWidth: '100%' }}>{collection}</Text>
            </Checkbox>
            <Text type="secondary" style={{ whiteSpace: 'nowrap' }}>
              {countAllowedFields(fields, hidden, globalHidden)}/{fields.length} permitidos
            </Text>
          </>
        ) : (
          <Text>Campos permitidos</Text>
        )}
      </div>

      {!collection ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Marca una colección permitida para ver sus campos" />
        </div>
      ) : (
        <>
          <div style={{ padding: 8 }}>
            <Input
              allowClear
              prefix={<SearchOutlined />}
              placeholder="Buscar campo"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div style={{ flex: 1, overflowY: 'auto', padding: '0 12px' }}>
            {loading ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}>
                <Spin />
              </div>
            ) : visible.length === 0 ? (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Sin campos" />
            ) : (
              visible.map((field) => {
                const isGlobal = globalHidden.has(field);
                return (
                  <div key={field} style={{ padding: '4px 0' }}>
                    <Checkbox
                      checked={!isGlobal && !hidden.includes(field)}
                      disabled={isGlobal}
                      onChange={(e) => onChange(setFieldAllowed(hidden, field, e.target.checked))}
                    >
                      {field}
                    </Checkbox>
                    {isGlobal && <Tag style={{ marginLeft: 4 }}>global</Tag>}
                  </div>
                );
              })
            )}
          </div>
          <div style={{ padding: 8, borderTop: DIVIDER }}>
            <Space.Compact style={{ width: '100%' }}>
              <Input
                placeholder="Ocultar otro campo"
                value={manual}
                onChange={(e) => setManual(e.target.value)}
                onPressEnter={addManual}
              />
              <Button aria-label="Ocultar campo" icon={<PlusOutlined />} onClick={addManual} />
            </Space.Compact>
          </div>
        </>
      )}
    </div>
  );
};

export default McpFieldPicker;
